import { createContext, useEffect, useState } from "react";
import { SessionContextValue, SessionProviderProps } from "./types";
import { useLocation } from "react-router-dom";

/**
 * SubSessionProvider забезпечує збереження стану таблиці (tableStateSub) 
 * та сесійних даних (sessionDataSub) у sessionStorage для окремої вкладки або розділу.
 *
 * 🟡 УВАГА:
 * Щоб `SubSessionContext` працював коректно:
 * 🔹 Компоненти, які використовують `tableStateSub` або `sessionDataSub`,
 *     мають бути **обгорнуті в `SubSessionProvider`**.
 * 🔹 Це означає, що ваш компонент повинен бути **всередині дерева JSX** провайдера:
 *
 * ✅ Правильно:
 * ```tsx
 * <SubSessionProvider nameSub="my-sub">
 *   <MySubTable />
 * </SubSessionProvider>
 * ```
 *
 * ❌ Неправильно:
 * ```tsx
 * <MySubTable /> // Поза провайдером, context не працюватиме
 * ```
 *
 * 🔄 Дані автоматично зберігаються в sessionStorage за ключами, 
 * що залежать від поточного маршруту (`route`) та `nameSub`.
 */

export const SubSessionContext = createContext<SessionContextValue>({
  tableStateSub: { selectedRow: undefined, pageSize: 15, currentPage: 1 },
  setTableStateSub: () => {},
  sessionDataSub: {},
  setSessionDataSub: () => {},
});

export function SubSessionProvider({ children, url, nameSub }: SessionProviderProps) {
  const location = useLocation();
  const route = url || location.pathname;
  const [tableStateSub, setTableStateSub] = useState(
    JSON.parse(sessionStorage.getItem(`table_state_${nameSub}${route}`) ?? "{}") || {
      selectedRow: undefined,
      pageSize: 15,
      currentPage: 1,
    }
  );
  const [sessionDataSub, setSessionDataSub] = useState(
    JSON.parse(sessionStorage.getItem(`session_data_${nameSub}${route}`) ?? "{}") || {}
  );

  useEffect(() => {
    Object.keys(tableStateSub).length > 0?  
    nameSub && sessionStorage.setItem(
      `table_state_${nameSub}${route}`,
      JSON.stringify(tableStateSub)
    ): sessionStorage.removeItem(`table_state_${nameSub}${route}`);
  }, [tableStateSub, route]);

  useEffect(() => {
    Object.keys(sessionDataSub).length > 0?  
    nameSub && sessionStorage.setItem(
      `session_data_${nameSub}${route}`,
      JSON.stringify(sessionDataSub)
    ): sessionStorage.removeItem(`session_data_${nameSub}${route}`);
  }, [sessionDataSub, route]);

  return (
    <SubSessionContext.Provider
      value={{
        tableStateSub,
        setTableStateSub,
        sessionDataSub,
        setSessionDataSub,
      }}
    >
      {children}
    </SubSessionContext.Provider>
  );
}
